<template>
  <div class="orderTasksContainer">
    <v-progress-circular indeterminate v-if="loading" />

    <div class="taskTileGrid" v-else>
      <div :key="index" v-for="(task, index) in tasks" class="taskTileCol">
        <TaskDetailsCard
            v-if="task && task.type === 'task'"
            :task="task.data"
        />

        <ImportLiveTaskCard
            v-else-if="task && task.type === 'shopifyTask'"
            :task="task.data"
        />

        <v-card class="taskNotPushedCard" v-else>
          <h2>
            Task not Imported <br />
            into TaskDash yet
          </h2>
        </v-card>

        <!-- ═══ Positions-Panel: Menge, Extras & Aktionen direkt am Task ═══ -->
        <div v-if="task" class="taskPanel">
          <div class="taskPanel__row">
            <div class="taskPanel__qty">
              <span class="taskPanel__label">Menge</span>
              <v-btn
                  icon x-small
                  :disabled="qtyFor(task) <= 1"
                  @click="bumpQty(task, -1)"
              >
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
              <span class="taskPanel__qtyValue">{{ qtyFor(task) }}</span>
              <v-btn icon x-small @click="bumpQty(task, 1)">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
              <v-btn
                  v-if="qtyDirty(task)"
                  x-small depressed color="primary" class="ml-1"
                  :loading="savingQty === taskUidOf(task)"
                  @click="saveQty(task)"
              >
                Speichern
              </v-btn>
            </div>

            <span
                class="taskPanel__chip"
                :class="entryFor(task).fulfilled ? 'taskPanel__chip--ok' : 'taskPanel__chip--wait'"
            >
              {{ entryFor(task).fulfilled ? "fulfilled" : "offen" }}
            </span>

            <v-spacer />

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    v-if="task.type === 'task'"
                    icon small v-bind="attrs" v-on="on"
                    @click="openDuplicateDialog(task.data)"
                >
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Task duplizieren</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openExtraDialog(task)">
                  <v-icon small>mdi-gift-outline</v-icon>
                </v-btn>
              </template>
              <span>Extra hinzufügen</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openDeleteDialog(task)">
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Task löschen</span>
            </v-tooltip>
          </div>

          <div v-if="(task.data.extras || []).length" class="taskPanel__extras">
            <span
                v-for="(ex, i) in task.data.extras"
                :key="i"
                class="taskPanel__extra"
            >
              <v-icon x-small class="mr-1">mdi-gift-outline</v-icon>
              {{ ex.name }} ×{{ ex.quantity || 1 }}
              <v-icon
                  x-small
                  class="taskPanel__extraRemove"
                  title="Extra entfernen"
                  @click="openRemoveExtraDialog(task, ex)"
              >mdi-close</v-icon>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══ Dialog: Extra hinzufügen (Mechanik analog taskdashAPI /changeProduct) ═══ -->
    <v-dialog v-model="extraDialog" max-width="460">
      <v-card>
        <v-card-title>Extra hinzufügen</v-card-title>
        <v-card-text>
          <p class="text--secondary mb-4" v-if="extraForm.taskUid">
            Für Task <strong>{{ extraForm.taskUid }}</strong>
          </p>
          <v-select
              v-model="extraForm.productId"
              :items="extraProductItems"
              label="Extra"
              dense outlined
              @change="extraForm.sku = null"
          />
          <v-select
              v-model="extraForm.sku"
              :items="extraVariantItems"
              label="Variante (SKU)"
              dense outlined
              :disabled="!extraForm.productId"
          />
          <v-text-field
              v-model.number="extraForm.quantity"
              label="Menge"
              type="number" min="1"
              dense outlined
          />
          <p class="text--secondary mb-0">
            Wird am Task und an der Bestellung hinterlegt; die Position wird auf
            „offen“ gesetzt, damit das Extra mitproduziert wird.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="extraDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn
              color="primary" depressed
              :disabled="!extraForm.productId || !extraForm.sku"
              :loading="savingExtra"
              @click="addExtra"
          >
            Hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog: Extra entfernen ═══ -->
    <v-dialog v-model="removeExtraDialog" max-width="440">
      <v-card>
        <v-card-title>Extra entfernen</v-card-title>
        <v-card-text v-if="removeExtraTarget">
          <p>
            Entfernt <strong>{{ removeExtraTarget.extra.name }}</strong>
            (×{{ removeExtraTarget.extra.quantity || 1 }}) von Task
            <strong>{{ taskUidOf(removeExtraTarget.task) }}</strong> —
            am Task und an der Bestellung.
          </p>
          <p class="text--secondary mb-0">
            Die Position wird auf „offen“ gesetzt, damit ohne das Extra
            produziert wird.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="removeExtraDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="error" depressed :loading="removingExtra" @click="removeExtra">
            <v-icon small left>mdi-close</v-icon> Entfernen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog: Task duplizieren ═══ -->
    <v-dialog v-model="duplicateDialog" max-width="460">
      <v-card>
        <v-card-title>Task duplizieren</v-card-title>
        <v-card-text v-if="duplicateSource">
          <p>
            Erstellt eine Kopie von <strong>{{ duplicateSource.uid }}</strong>
            als neuen Task <strong>{{ duplicateNewId || "…" }}</strong>.
          </p>
          <p class="mb-1">
            Variante aktuell: <strong>{{ duplicateSource.printVariantName || "—" }}</strong>
          </p>
          <p class="text--secondary mb-0">
            Größe/Variante danach im neuen Task anpassen. Der neue Task erhält den
            Status {{ duplicateStatus }} und ist noch nicht gefulfillt.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="duplicateDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn
              color="primary"
              depressed
              :loading="duplicating"
              :disabled="!duplicateNewId"
              @click="duplicateTask"
          >
            Duplizieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- ═══ Dialog: Task löschen ═══ -->
    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card>
        <v-card-title>Task löschen</v-card-title>
        <v-card-text v-if="deleteTarget">
          <p>
            Löscht <strong>{{ taskUidOf(deleteTarget) }}</strong> endgültig —
            das Task-Dokument und die Position an der Bestellung.
          </p>
          <p class="text--secondary mb-0">Das kann nicht rückgängig gemacht werden.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="error" depressed :loading="deleting" @click="deleteTask">
            <v-icon small left>mdi-delete-outline</v-icon> Endgültig löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { deleteDoc, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../../fb";
import { taskStatus } from "@/constants";
import { EventBus, events } from "../../../../event-bus";
import dayjs from "dayjs";

import TaskDetailsCard from "@/components/TaskCards/TaskDetailsCard";
import ImportLiveTaskCard from "@/components/TaskCards/ImportLiveTaskCard";

export default {
  name: "OrderTasks",

  components: {
    TaskDetailsCard,
    ImportLiveTaskCard,
  },

  data() {
    return {
      loading: false,
      tasks: [],
      // Menge (orders.tasks[].quantity)
      qtyDraft: {},
      savingQty: null,
      // Extras
      extraDialog: false,
      extrasCatalog: {},
      extraForm: { productId: null, sku: null, taskUid: null, quantity: 1 },
      savingExtra: false,
      removeExtraDialog: false,
      removeExtraTarget: null, // { task, extra }
      removingExtra: false,
      // Löschen
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      // Duplizieren
      duplicateDialog: false,
      duplicateSource: null,
      duplicateNewId: null,
      duplicating: false,
    };
  },

  props: {
    orderTasks: {
      required: true,
    },
    orderId: {
      type: [String, Number],
      required: false,
    },
  },

  computed: {
    duplicateStatus() {
      // Fertige/abgeschlossene Originale werden als CLOSED dupliziert
      // (produktionsfertig, aber nicht versandt) — alles andere 1:1
      const st = this.duplicateSource?.status;
      return st === taskStatus.FULFILLED || st === taskStatus.CANCELED ? taskStatus.CLOSED : st;
    },
    extraProductItems() {
      return Object.entries(this.extrasCatalog).map(([id, e]) => ({
        text: e.name || id,
        value: id,
      }));
    },
    extraVariantItems() {
      const e = this.extrasCatalog[this.extraForm.productId];
      if (!e || !e.variants) return [];
      return Object.keys(e.variants).map((sku) => ({ text: sku, value: sku }));
    },
  },

  mounted() {
    this.loadLineItems();
  },

  methods: {
    async loadLineItems() {
      this.loading = true;
      this.tasks = [];

      for await (const task of this.orderTasks) {
        const taskUid = task.taskUid;

        const taskDoc = doc(db, `tasks/${taskUid}`);
        const taskRef = await getDoc(taskDoc);

        if (taskRef.exists()) {
          this.tasks.push({
            type: "task",
            data: {
              id: taskRef.id,
              ...taskRef.data(),
            },
          });

          continue;
        }

        const shopifyTaskDoc = doc(db, `shopifyTasks/${taskUid}`);
        const shopifyTaskRef = await getDoc(shopifyTaskDoc);

        if (shopifyTaskRef.exists()) {
          this.tasks.push({
            type: "shopifyTask",
            data: {
              id: shopifyTaskRef.id,
              ...shopifyTaskRef.data(),
            },
          });

          continue;
        }

        this.tasks.push(null);
      }

      this.loading = false;
    },

    /* ── Zuordnung Task ↔ orders.tasks[]-Eintrag ── */
    taskUidOf(task) {
      return task.data.uid || task.data.id;
    },
    entryFor(task) {
      const uid = this.taskUidOf(task);
      return (this.orderTasks || []).find((t) => t.taskUid === uid) || {};
    },
    resolvedOrderId(task) {
      return String(this.orderId || task.data.orderId || "");
    },

    /* ── Menge (orders.tasks[].quantity) ── */
    qtyFor(task) {
      const uid = this.taskUidOf(task);
      return this.qtyDraft[uid] || this.entryFor(task).quantity || 1;
    },
    qtyDirty(task) {
      const uid = this.taskUidOf(task);
      return this.qtyDraft[uid] && this.qtyDraft[uid] !== (this.entryFor(task).quantity || 1);
    },
    bumpQty(task, delta) {
      const uid = this.taskUidOf(task);
      this.$set(this.qtyDraft, uid, Math.max(1, this.qtyFor(task) + delta));
    },
    async saveQty(task) {
      const uid = this.taskUidOf(task);
      this.savingQty = uid;
      try {
        const newQty = this.qtyDraft[uid];
        const orderRef = doc(db, `orders/${this.resolvedOrderId(task)}`);
        const snap = await getDoc(orderRef);
        if (!snap.exists()) throw new Error("Order nicht gefunden");
        // Mengenänderung setzt die Position auf offen, damit der Fulfiller
        // die neue Menge produziert (gleiches Muster wie /changeProduct)
        const tasks = (snap.data().tasks || []).map((x) =>
          x.taskUid === uid ? { ...x, quantity: newQty, fulfilled: false } : x
        );
        await setDoc(orderRef, { tasks }, { merge: true });
        this.$delete(this.qtyDraft, uid);
        EventBus.$emit(events.SNACKBAR, { message: `Menge für ${uid} → ${newQty}`, type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Menge speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.savingQty = null;
      }
    },

    /* ── Extras (Mechanik analog taskdashAPI /changeProduct) ── */
    async openExtraDialog(task) {
      this.extraForm = {
        productId: null,
        sku: null,
        taskUid: this.taskUidOf(task),
        quantity: 1,
      };
      this._extraTask = task;
      this.extraDialog = true;
      if (!Object.keys(this.extrasCatalog).length) {
        const snap = await getDoc(doc(db, "settings/extras"));
        this.extrasCatalog = snap.exists() ? snap.data() : {};
      }
    },
    async addExtra() {
      this.savingExtra = true;
      try {
        const { productId, sku, taskUid, quantity } = this.extraForm;
        const catalogEntry = this.extrasCatalog[productId] || {};
        const newExtra = {
          sku,
          name: catalogEntry.name || sku,
          product_id: productId,
          quantity: quantity || 1,
        };

        // Task-Doc finden (tasks ODER shopifyTasks — wie /changeProduct)
        let taskRef = doc(db, `tasks/${taskUid}`);
        let taskSnap = await getDoc(taskRef);
        if (!taskSnap.exists()) {
          taskRef = doc(db, `shopifyTasks/${taskUid}`);
          taskSnap = await getDoc(taskRef);
          if (!taskSnap.exists()) throw new Error(`Task ${taskUid} nicht gefunden`);
        }
        const taskExtras = Array.isArray(taskSnap.data().extras) ? taskSnap.data().extras : [];
        await setDoc(taskRef, { extras: [...taskExtras, newExtra] }, { merge: true });

        // Order-Doc: Extras ergänzen + Position auf offen
        const orderRef = doc(db, `orders/${this.resolvedOrderId(this._extraTask)}`);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          const orderExtras = Array.isArray(orderData.extras) ? orderData.extras : [];
          const tasks = (orderData.tasks || []).map((x) =>
            x.taskUid === taskUid ? { ...x, fulfilled: false } : x
          );
          await setDoc(orderRef, { extras: [...orderExtras, newExtra], tasks }, { merge: true });
        }

        EventBus.$emit(events.SNACKBAR, { message: `Extra ${newExtra.name} hinzugefügt`, type: "success" });
        this.extraDialog = false;
        await this.loadLineItems();
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Extra fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.savingExtra = false;
      }
    },

    /* ── Extra entfernen (Gegenstück zu addExtra: Task-Doc + Order-Doc) ── */
    openRemoveExtraDialog(task, extra) {
      this.removeExtraTarget = { task, extra };
      this.removeExtraDialog = true;
    },
    async removeExtra() {
      this.removingExtra = true;
      try {
        const { task, extra } = this.removeExtraTarget;
        const taskUid = this.taskUidOf(task);
        // Genau EIN passender Eintrag wird entfernt (nicht alle mit gleicher
        // SKU — es kann z. B. zwei gleiche Rahmen auf verschiedenen Tasks geben)
        const matches = (x) =>
          String(x.sku) === String(extra.sku) &&
          String(x.product_id) === String(extra.product_id);
        const removeOne = (list, pref) => {
          const arr = Array.isArray(list) ? [...list] : [];
          let idx = pref ? arr.findIndex((x) => matches(x) && pref(x)) : -1;
          if (idx === -1) idx = arr.findIndex(matches);
          if (idx !== -1) arr.splice(idx, 1);
          return { arr, found: idx !== -1 };
        };

        // Task-Doc finden (tasks ODER shopifyTasks — wie addExtra)
        let taskRef = doc(db, `tasks/${taskUid}`);
        let taskSnap = await getDoc(taskRef);
        if (!taskSnap.exists()) {
          taskRef = doc(db, `shopifyTasks/${taskUid}`);
          taskSnap = await getDoc(taskRef);
          if (!taskSnap.exists()) throw new Error(`Task ${taskUid} nicht gefunden`);
        }
        const { arr: taskExtras, found } = removeOne(taskSnap.data().extras);
        if (!found) throw new Error(`Extra ${extra.sku} am Task nicht gefunden`);
        await setDoc(taskRef, { extras: taskExtras }, { merge: true });

        // Order-Doc: denselben Eintrag entfernen (bevorzugt den, der über
        // belongs_to_task an genau diesem Task hängt) + Position auf offen
        const orderRef = doc(db, `orders/${this.resolvedOrderId(task)}`);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          const { arr: orderExtras } = removeOne(
            orderData.extras,
            (x) => String(x.belongs_to_task || "") === String(taskUid)
          );
          const tasks = (orderData.tasks || []).map((x) =>
            x.taskUid === taskUid ? { ...x, fulfilled: false } : x
          );
          await setDoc(orderRef, { extras: orderExtras, tasks }, { merge: true });
        }

        EventBus.$emit(events.SNACKBAR, { message: `Extra ${extra.name} entfernt`, type: "success" });
        this.removeExtraDialog = false;
        await this.loadLineItems();
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Entfernen fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.removingExtra = false;
      }
    },

    /* ── Task löschen (Task-Doc + Positions-Eintrag an der Bestellung) ── */
    openDeleteDialog(task) {
      this.deleteTarget = task;
      this.deleteDialog = true;
    },
    async deleteTask() {
      this.deleting = true;
      try {
        const task = this.deleteTarget;
        const uid = this.taskUidOf(task);

        // Task-Doc löschen — in der Collection, aus der er geladen wurde
        const coll = task.type === "shopifyTask" ? "shopifyTasks" : "tasks";
        await deleteDoc(doc(db, `${coll}/${uid}`));

        // Positions-Eintrag aus orders.tasks[] entfernen
        const orderRef = doc(db, `orders/${this.resolvedOrderId(task)}`);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const tasks = (orderSnap.data().tasks || []).filter((x) => x.taskUid !== uid);
          await setDoc(orderRef, { tasks }, { merge: true });
        }

        EventBus.$emit(events.SNACKBAR, { message: `Task ${uid} gelöscht`, type: "success" });
        this.deleteDialog = false;
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Löschen fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.deleting = false;
      }
    },

    /** Freie Task-ID finden: orderId_N mit N über allen bekannten Suffixen —
     *  und vor der Vergabe wird JEDE Kandidaten-ID gegen tasks UND
     *  shopifyTasks geprüft, damit nie ein bestehender Task überschrieben
     *  werden kann (auch nicht bei Lücken oder Live-Editor-Tasks). */
    async findFreeTaskId(orderId) {
      let max = 0;
      for (const t of this.orderTasks || []) {
        const m = /_(\d+)$/.exec(String(t.taskUid || ""));
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
      for (let n = max + 1; n < max + 60; n++) {
        const candidate = `${orderId}_${n}`;
        const [a, b] = await Promise.all([
          getDoc(doc(db, `tasks/${candidate}`)),
          getDoc(doc(db, `shopifyTasks/${candidate}`)),
        ]);
        if (!a.exists() && !b.exists()) return candidate;
      }
      throw new Error("Keine freie Task-ID gefunden");
    },

    async openDuplicateDialog(taskData) {
      this.duplicateSource = taskData;
      this.duplicateNewId = null;
      this.duplicateDialog = true;
      try {
        this.duplicateNewId = await this.findFreeTaskId(taskData.orderId);
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: String(e.message || e), type: "error" });
        this.duplicateDialog = false;
      }
    },

    async duplicateTask() {
      this.duplicating = true;
      try {
        const src = this.duplicateSource;
        const newId = this.duplicateNewId;
        // Letzter Guard direkt vor dem Schreiben (Dialog kann länger offen sein)
        const [a, b] = await Promise.all([
          getDoc(doc(db, `tasks/${newId}`)),
          getDoc(doc(db, `shopifyTasks/${newId}`)),
        ]);
        if (a.exists() || b.exists()) throw new Error(`${newId} existiert inzwischen — bitte erneut öffnen`);

        const { id: _dropId, ...copy } = src;
        await setDoc(doc(db, `tasks/${newId}`), {
          ...copy,
          uid: newId,
          status: this.duplicateStatus,
          assignedTo: null,
          createdAt: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
          duplicatedFrom: src.uid,
        });

        // Order-Doc: Task-Eintrag ergänzen (sku/quantity vom Original)
        const orderRef = doc(db, `orders/${src.orderId}`);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderTasks = Array.isArray(orderSnap.data().tasks) ? orderSnap.data().tasks : [];
          const origEntry = orderTasks.find((t) => t.taskUid === src.uid) || {};
          const newEntry = {
            taskUid: newId,
            sku: origEntry.sku || null,
            quantity: origEntry.quantity || 1,
            fulfilled: false,
          };
          await setDoc(orderRef, { tasks: [...orderTasks, newEntry] }, { merge: true });
          this.orderTasks.push(newEntry);
        }

        EventBus.$emit(events.SNACKBAR, { message: `Task ${newId} erstellt`, type: "success" });
        this.duplicateDialog = false;
        await this.loadLineItems();
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Duplizieren fehlgeschlagen: ${e.message || e}`, type: "error" });
      } finally {
        this.duplicating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.orderTasksContainer {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: 12px 0 0;
}

.taskNotPushedCard {
  height: 100%;
  min-height: 400px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* ── Positions-Panel unter jeder Task-Karte ── */
.taskPanel {
  background: transparent;
  border: none;
  border-radius: 0 0 14px 14px;
  padding: 10px 14px;
  margin-top: 0;

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
    margin-right: 2px;
  }
  &__qty {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  &__qtyValue {
    min-width: 22px;
    text-align: center;
    font-weight: 700;
    font-size: 14px;
  }
  &__chip {
    font-size: 11px;
    font-weight: 700;
    border-radius: 999px;
    padding: 2px 10px;

    &--ok {
      background: #e8f5e9;
      color: #2e7d32;
    }
    &--wait {
      background: #fff3e0;
      color: #b26a00;
    }
  }
  &__extras {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  &__extra {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 999px;
    padding: 2px 10px;
  }

  &__extraRemove {
    margin-left: 6px;
    cursor: pointer;
    opacity: 0.45;
    transition: opacity 0.15s, color 0.15s;

    &:hover {
      opacity: 1;
      color: #ef4444 !important;
    }
  }
}

/* Fixes 2er-Grid: immer zwei Kacheln pro Reihe (mobil eine) —
   minmax(0, 1fr) zwingt die Spaltenbreite auch gegen sperrigen
   Karten-Inhalt (min-content) durch */
.taskTileGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1420px;
}
@media (max-width: 900px) {
  .taskTileGrid {
    grid-template-columns: minmax(0, 1fr);
  }
}
.taskTileCol {
  min-width: 0;
  /* Karte + Positions-Panel als EINE Einheit: gemeinsamer Rahmen/Schatten,
     innen verlieren beide ihre eigenen Kanten */
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  background: #faf9fc;
  overflow: hidden;
  align-self: start;

  /* Karte bleibt rundum gerundet und liegt AUF der Einheit-Fläche */
  ::v-deep .v-card {
    border-radius: 14px !important;
    box-shadow: none !important;
  }
}
</style>
